import React from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import NewBadge from 'src/components/NewBadge/NewBadge';
import { AssignableRole } from 'src/common/roleRegistry/roleRegistry';
import { useChangeUserRoleOrchestrator } from 'src/components/ContactFormComponents/ChangeUserRoleDialogue/CustomHooks/useChangeUserRoleOrchestrator';
import CurrentRoleSummary from 'src/components/ContactFormComponents/ChangeUserRoleDialogue/components/CurrentRoleSummary';
import RoleSelectionList from 'src/components/ContactFormComponents/ChangeUserRoleDialogue/components/RoleSelectionList';
import RoleChangeCallouts from 'src/components/ContactFormComponents/ChangeUserRoleDialogue/components/RoleChangeCallouts';
import RoleChangeFooter from 'src/components/ContactFormComponents/ChangeUserRoleDialogue/components/RoleChangeFooter';
import './change-user-role-dialogue.scss';

export interface ChangeUserRoleContact {
  id: string;
  firstname: string;
  lastname: string;
  profileImage?: string | null;
  userRole: string;
}

export interface ChangeUserRoleDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  contact: ChangeUserRoleContact;
  onSuccess?: (newRole: AssignableRole) => void;
}

const ChangeUserRoleDialogue = ({
  isOpen,
  onClose,
  contact,
  onSuccess,
}: ChangeUserRoleDialogueProps) => {
  const {
    availableRoles,
    selectedRole,
    setSelectedRole,
    callouts,
    submit,
    reset,
    isSubmitting,
    isValid,
    error,
  } = useChangeUserRoleOrchestrator({
    contactId: contact.id,
    currentRole: contact.userRole,
    onSuccess: (newRole: AssignableRole) => {
      onSuccess?.(newRole);
      handleClose();
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const fullName: string = `${contact.firstname} ${contact.lastname}`.trim();

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <>
          Change user role
          <NewBadge spaced />
        </>
      }
      description={`Update the role assigned to ${fullName}.`}
      maxWidth="70rem"
      avatar={
        contact.profileImage ? (
          <img
            src={contact.profileImage}
            alt={fullName}
            className="change-user-role-dialogue-avatar-img"
          />
        ) : undefined
      }
      footer={
        <RoleChangeFooter
          onConfirm={() => {
            void submit();
          }}
          onCancel={handleClose}
          isSubmitting={isSubmitting}
          isConfirmDisabled={!isValid}
        />
      }
    >
      <div className="change-user-role-dialogue-body">
        <CurrentRoleSummary currentRole={contact.userRole} />
        <RoleSelectionList
          availableRoles={availableRoles}
          selectedRole={selectedRole}
          onSelect={setSelectedRole}
        />
        <RoleChangeCallouts callouts={callouts} />
        {error && (
          <div className="change-user-role-dialogue-error">{error}</div>
        )}
      </div>
    </GenericDialogue>
  );
};

export default ChangeUserRoleDialogue;
